import { useEffect, useState } from 'react'
import { Megaphone, Loader2, AlertCircle, AlertTriangle, Info, Eye } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useAdminAnnouncement,
  useUpdateAnnouncement,
  type AnnouncementSeverity,
  type UpdateAnnouncementDTO,
} from '@/services/system-announcement.service'
import { useToast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/utils/error'

const SEVERITY_OPTIONS: Array<{
  value: AnnouncementSeverity
  label: string
  description: string
  icon: typeof Info
  color: string
}> = [
  {
    value: 'info',
    label: 'Informativo',
    description: 'Mensagem neutra, azul',
    icon: Info,
    color: 'text-sky-400 border-sky-500/40 bg-sky-500/10',
  },
  {
    value: 'warning',
    label: 'Atenção',
    description: 'Mudança planejada, laranja',
    icon: AlertTriangle,
    color: 'text-amber-400 border-amber-500/40 bg-amber-500/10',
  },
  {
    value: 'critical',
    label: 'Crítico',
    description: 'Manutenção / downtime, laranja pulsante',
    icon: AlertCircle,
    color: 'text-[#f97e00] border-[#f97e00]/40 bg-[#f97e00]/10',
  },
]

/**
 * Tela `Sistema → Banner global` no super_admin.
 *
 * Permite ativar/desativar e editar a mensagem do banner que aparece
 * no topo pra TODOS os usuários (incluindo deslogados). Polling do
 * front é a cada 60s — mudança propaga em até 1 minuto.
 *
 * Casos de uso: avisar janela de manutenção, downtime planejado,
 * lançamento de feature, comunicado de empresa.
 */
export function SystemAnnouncementManagement() {
  const { data, isLoading, isError } = useAdminAnnouncement()
  const update = useUpdateAnnouncement()
  const { toast } = useToast()

  const [draft, setDraft] = useState<UpdateAnnouncementDTO>({})

  useEffect(() => {
    if (data && Object.keys(draft).length === 0) {
      setDraft({
        message: data.message,
        active: data.active,
        severity: data.severity,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const message = draft.message ?? data?.message ?? ''
  const active = draft.active ?? data?.active ?? false
  const severity = (draft.severity ?? data?.severity ?? 'info') as AnnouncementSeverity
  const isDirty =
    data &&
    (draft.message !== data.message ||
      draft.active !== data.active ||
      draft.severity !== data.severity)

  async function handleSave() {
    try {
      await update.mutateAsync(draft)
      toast({
        title: 'Banner atualizado',
        description: active
          ? 'Mensagem visível pra todos em até 60s.'
          : 'Banner desativado. Some pros usuários em até 60s.',
      })
    } catch (err) {
      toast({
        title: 'Erro ao salvar',
        description: getErrorMessage(err),
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50">
        <CardHeader>
          <Skeleton className="h-6 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-48" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="glass-card border-border/50">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Erro ao carregar configuração do banner.
        </CardContent>
      </Card>
    )
  }

  const SeverityIcon = SEVERITY_OPTIONS.find((o) => o.value === severity)?.icon ?? Info

  return (
    <Card className="glass-card border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-primary" />
          Banner global de aviso
        </CardTitle>
        <CardDescription>
          Mensagem fixa no topo da página pra todos os usuários (incluindo deslogados). Use pra
          avisar manutenção, downtime planejado, comunicados importantes. Polling 60s — mudança
          propaga em até 1 minuto.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Toggle ativo */}
        <div className="flex items-center justify-between p-4 rounded-lg border border-border/50 bg-muted/20">
          <div className="space-y-0.5">
            <Label htmlFor="banner-active" className="text-base font-medium">
              Banner ativo
            </Label>
            <p className="text-xs text-muted-foreground">
              Quando desligado, o banner some pra todos. Mensagem fica salva.
            </p>
          </div>
          <Switch
            id="banner-active"
            checked={active}
            onCheckedChange={(v) => setDraft((d) => ({ ...d, active: v }))}
          />
        </div>

        {/* Severity */}
        <div className="space-y-2">
          <Label className="text-sm">Severidade</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {SEVERITY_OPTIONS.map((opt) => {
              const isSelected = severity === opt.value
              const Icon = opt.icon
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, severity: opt.value }))}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    isSelected
                      ? `${opt.color} ring-1 ring-current/30`
                      : 'border-border/50 bg-muted/10 hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${isSelected ? '' : 'text-muted-foreground'}`} />
                    <span className="text-sm font-medium">{opt.label}</span>
                  </div>
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    {opt.description}
                  </p>
                </button>
              )
            })}
          </div>
        </div>

        {/* Mensagem */}
        <div className="space-y-2">
          <Label htmlFor="banner-message" className="text-sm">
            Mensagem ({message.length}/500)
          </Label>
          <Textarea
            id="banner-message"
            value={message}
            onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value.slice(0, 500) }))}
            placeholder="Ex: Manutenção programada hoje das 20:00 às 20:30 BRT. Pode haver instabilidade."
            rows={3}
            className="resize-none"
          />
        </div>

        {/* Preview */}
        <div className="space-y-2">
          <Label className="text-sm flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" />
            Preview
          </Label>
          {message.trim() ? (
            <div
              className={`rounded-lg border px-4 py-3 flex items-center gap-3 ${
                severity === 'critical'
                  ? 'bg-gradient-to-r from-[#e06d00] via-[#f97e00] to-[#e06d00] border-[#f97e00]/60'
                  : severity === 'warning'
                    ? 'bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 border-amber-400/40'
                    : 'bg-gradient-to-r from-sky-600 via-sky-500 to-sky-600 border-sky-400/40'
              }`}
            >
              <SeverityIcon
                className={`w-5 h-5 text-white flex-shrink-0 ${
                  severity === 'critical' ? 'animate-pulse' : ''
                }`}
              />
              <p className="text-sm font-medium text-white leading-snug">{message}</p>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border/50 p-4 text-center text-xs text-muted-foreground">
              Sem mensagem — preview aparece quando você escrever algo.
            </div>
          )}
          {!active && message.trim() && (
            <p className="text-[11px] text-muted-foreground italic">
              ⓘ Banner está desligado. Mesmo com mensagem, não aparece pros usuários.
            </p>
          )}
        </div>

        {/* Save */}
        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={!isDirty || update.isPending} className="min-w-32">
            {update.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando…
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
